import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { categoryName } from "@/lib/data";
import { azError } from "@/lib/az-errors";
import {
  approvePromoRequest,
  fetchPromoRequests,
  promoPlanLabel,
  rejectPromoRequest,
  type AdminPromoRequest,
} from "@/lib/promo-requests";

export const Route = createFileRoute("/_authenticated/admin/muracietler")({
  head: () => ({
    meta: [
      { title: "İrəli çək / VIP müraciətləri — Albazar.az" },
      { name: "description", content: "İstifadəçilərin VIP, Premium və İrəli çək müraciətlərini idarə edin." },
      { property: "og:title", content: "Reklam müraciətləri — Albazar.az" },
      { property: "og:description", content: "VIP, Premium və İrəli çək müraciətləri." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PromoRequestsPage,
});

const statusLabel: Record<string, string> = {
  pending: "Gözləyir",
  approved: "Təsdiqləndi",
  rejected: "İmtina",
};

function PromoRequestsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["promo-requests"],
    queryFn: fetchPromoRequests,
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: ["promo-requests"] });

  const approve = async (req: AdminPromoRequest) => {
    try {
      await approvePromoRequest(req);
      toast.success("Müraciət təsdiqləndi");
      refresh();
    } catch (e) {
      toast.error(azError(e));
    }
  };

  const reject = async (req: AdminPromoRequest) => {
    try {
      await rejectPromoRequest(req.id);
      toast.success("Müraciətdən imtina edildi");
      refresh();
    } catch (e) {
      toast.error(azError(e));
    }
  };

  if (isLoading) {
    return (
      <div className="grid place-items-center py-20">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  const rows = data ?? [];
  const pending = rows.filter((r) => r.status === "pending");
  const done = rows.filter((r) => r.status !== "pending");

  return (
    <div>
      <h1 className="text-2xl font-extrabold tracking-tight">Reklam müraciətləri</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        İstifadəçilərin İrəli çək, VIP və Premium müraciətləri.
      </p>

      <h2 className="mt-6 text-sm font-bold uppercase tracking-wide text-muted-foreground">
        Gözləyən ({pending.length})
      </h2>
      <div className="mt-3 grid gap-3">
        {pending.length === 0 && (
          <p className="rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
            Gözləyən müraciət yoxdur.
          </p>
        )}
        {pending.map((r) => (
          <article
            key={r.id}
            className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-card sm:flex-row sm:items-center"
          >
            {r.listing_image && (
              <img
                src={r.listing_image}
                alt={r.listing_title}
                className="size-16 rounded-xl object-cover"
                loading="lazy"
              />
            )}
            <div className="min-w-0 flex-1">
              <Link
                to="/elan/$id"
                params={{ id: r.listing_id }}
                className="truncate font-bold hover:text-primary"
              >
                {r.listing_title}
              </Link>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {promoPlanLabel[r.plan]} · {r.plan === "bump" ? "yuxarı çəkilsin" : `${r.days} gün`} ·{" "}
                {categoryName(r.listing_category)} · {r.owner_name ?? "İstifadəçi"}
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="rounded-full" onClick={() => void approve(r)}>
                <Check className="size-4" /> Təsdiqlə
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10"
                onClick={() => void reject(r)}
              >
                <X className="size-4" /> İmtina
              </Button>
            </div>
          </article>
        ))}
      </div>

      {done.length > 0 && (
        <>
          <h2 className="mt-8 text-sm font-bold uppercase tracking-wide text-muted-foreground">
            Baxılmış ({done.length})
          </h2>
          <div className="mt-3 grid gap-2">
            {done.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card px-4 py-3 text-sm"
              >
                <span className="min-w-0 truncate">
                  {r.listing_title} · {promoPlanLabel[r.plan]}
                </span>
                <span className="shrink-0 text-xs font-semibold text-muted-foreground">
                  {statusLabel[r.status]}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
